/**
 * The Auto-approval surface — CLM-108 (FR-31, FR-32, NFR-16, CR-06). The build
 * decisions of 1 Oct 2026 (Harnoor Gandhi, Jira CLM-108 comment 1942022) are in
 * force: the CR-06 vocabulary, D-2.1-1/2/3 (CLM-107's grain, 30 decisions, a
 * model or schema change restarts the sample), D-2.1-4, D-2.1-5 (high-stakes
 * fields are always human) and D-2.1-7 (an Admin sets the threshold; a
 * different Legal holder makes it effective). R-108-1 … R-108-24 are the spec's
 * (`phase4-specs/CLM-108-per-field-verification-policy.md` §3.2).
 *
 * Nine endpoints:
 *
 * | Name | Gate | What it does |
 * |---|---|---|
 * | `verification-policy` | `admin:configure`; `fields` needs Scope-All | the settings card and the per-field table |
 * | `set-field-verification-policy` | `admin:configure` + Scope-All | a new per-field policy revision |
 * | `cosign-field-verification-policy` | `compliance:cosign_verification_settings` + Scope-All | Legal's decision on an opt-in |
 * | `propose-verification-settings` | `admin:configure` | a proposed threshold / sample rate / window |
 * | `withdraw-verification-settings` | `admin:configure` | withdraw the pending proposal |
 * | `verification-settings` | `compliance:cosign_verification_settings` | Legal's view of the settings |
 * | `cosign-verification-settings` | `compliance:cosign_verification_settings` | co-sign or decline the pending proposal |
 * | `self-certify-verification-settings` | `admin:configure` + Scope-All + flag | **CLM-127, D-127-1** — propose and decide the settings in one transaction, no Legal co-sign |
 * | `self-certify-field-verification-policy` | `admin:configure` + Scope-All + flag | **CLM-127, D-127-1** — propose and decide an `auto_approve` opt-in in one transaction |
 *
 * ## CLM-127, D-127-1 — self-certify, flag-gated, off by default
 *
 * `CEQ-Legal` is not yet using CLM, so nobody can ever hold `legal_reviewer` in
 * a way that reaches this application, and the two endpoints above exist for
 * exactly that gap: a Scope-All Admin performs the proposal and its own
 * decision in one request, gated on `CLM_API_VERIFICATION_POLICY_ALLOW_SELF_CERTIFY`
 * (unset — the default — refuses the action entirely, falling back to today's
 * two-person behaviour). Every row either path writes carries
 * `is_self_certified`, always visibly distinct from a genuine co-sign on every
 * surface that reads it — never "co-signed by Legal", always "self-certified
 * by an Admin, not independently reviewed". The normal two-person path is
 * extended, never narrowed: `ck_verification_policy_setting_two_people` and
 * `ck_verification_policy_cosign_two_people` (`packages/db`) refuse a
 * same-person row unless the marker is true, and now also refuse the marker
 * being true on a genuinely different-person row, so it can never
 * misrepresent which happened. Reversible by the one flag: turning it off
 * stops the action from being offered again; rows already self-certified stay
 * exactly as written.
 *
 * ## Nothing here decides a field
 *
 * The pipeline's own state statement, read inside the store transaction, is the
 * only read that decides whether a field auto-approves (R-108-2). These
 * endpoints configure the policy and show its figures; the page's
 * `effective_state` is the same decision with the sample step dropped.
 *
 * ## Exact values on the wire (R-108-5)
 *
 * The threshold, the sample rate and every rate are `numeric(4,3)` decimal
 * strings (`"0.900"`), never numbers: `Number("0.935") * 1000` need not be an
 * integer, and no float may touch the threshold. Every count is exact.
 *
 * ## Who is recorded, and how
 *
 * No request carries a UPN, an object id, a count or a basis. The server binds
 * the people from the session (`clm.actor_upn`, `clm.actor_object_id`) and
 * captures the org-wide basis itself, so a client cannot attest to figures it
 * computed or to someone else's identity; the strict bodies make an attempt a
 * `400`, never a silent no-op. The database checks the same thing again — the
 * person on each row must be the session's actor with the role the act needs
 * (R-108-23 (5)), and the co-signer must differ from the proposer by UPN and
 * object id (R-108-10, R-108-24). Object ids never leave the server:
 * `caller_is_proposer` / `caller_is_setter` are computed there.
 *
 * ## Scope (R-108-12)
 *
 * The policy runs on the org-wide figure (D-107-1), so everything that shows or
 * decides on it needs Scope-All in addition to its action: a practice-scoped
 * Admin gets `fields: null` with `fields_withheld: 'scope_all_required'` and
 * still sees the settings card (configuration, not a fact about any
 * contract); the per-field write and the opt-in co-sign answer `403`; Legal's
 * `impact` and `pending_opt_ins` are `null`.
 *
 * ## Refusals
 *
 * Every refusal is a coded `field_errors` entry (`code: sentence`), CLM-109's
 * shape; the codes are below, grouped by HTTP status.
 */
import { z } from 'zod';
import { FieldNameSchema } from '../domain/verification.js';
import { AuditReceiptSchema } from '../provenance.js';
import { bigintIdTextSchema, IsoTimestampUtcSchema, UpnSchema } from '../primitives.js';
import { ThresholdSchema, VERIFICATION_MINIMUM_SAMPLE, VerificationPolicyModeSchema, VerificationPolicyStateSchema, } from '../vocabulary.js';
/* -------------------------------------------------------------------------- */
/* Vocabulary                                                                  */
/* -------------------------------------------------------------------------- */
/** `clm.verification_policy_setting.status` (R-108-10). */
export const VERIFICATION_SETTING_STATUSES = ['pending', 'effective', 'declined', 'withdrawn'];
export const VerificationSettingStatusSchema = z.enum(VERIFICATION_SETTING_STATUSES);
/** Legal's two answers to a pending settings change or an opt-in. */
export const VERIFICATION_COSIGN_DECISIONS = ['cosign', 'decline'];
export const VerificationCosignDecisionSchema = z.enum(VERIFICATION_COSIGN_DECISIONS);
/** Why `fields` is absent from the page: the caller is not Scope-All (R-108-12). */
export const VERIFICATION_POLICY_FIELDS_WITHHELD = ['scope_all_required'];
/**
 * The database's sanity bounds on the settings (R-108-9) — not policy, which is
 * whatever Legal co-signs inside them. Exported so the route can name a refusal
 * before the CHECK does and the page can bound its inputs. The threshold's lower
 * bound is exclusive.
 */
export const VERIFICATION_SETTING_BOUNDS = {
    threshold_above: '0.500',
    threshold_max: '1.000',
    sample_rate_min: '0.050',
    sample_rate_max: '1.000',
    stale_after_days_min: 30,
    stale_after_days_max: 365,
};
/** 400 — the change cannot be made as asked. */
export const VERIFICATION_POLICY_VALIDATION_CODES = [
    /** D-2.1-5: a high-stakes field accepts no policy row at all. */
    'high_stakes_exempt',
    'no_change',
    /** An opt-in, or a decline, needs a reason. */
    'note_required',
    'threshold_out_of_bounds',
    'sample_rate_out_of_bounds',
    'stale_window_out_of_bounds',
];
/** 404 — the field is neither governed nor ever added (CLM-109). */
export const VERIFICATION_POLICY_NOT_FOUND_CODES = ['field_not_found'];
/** 409 — the state does not admit the change, or moved under the caller. */
export const VERIFICATION_POLICY_CONFLICT_CODES = [
    'field_retired',
    /** D-2.1-2: an opt-in still needs 30 decisions at the current grain. */
    'too_few_decisions',
    /** The expected revision is stale, or the 23505 on uq_verification_policy_revision. */
    'policy_changed',
    'not_opt_in',
    /** The opt-in's basis grain is no longer the field's latest pipeline grain. */
    'opt_in_lapsed',
    /** Same UPN in any letter case, or same object id — a dual-role caller co-signing their own change included. */
    'cosigner_is_proposer',
    /** The 23505 on the co-sign row's primary key. */
    'already_decided',
    /** The 23505 on ux_verification_policy_setting_one_pending. */
    'change_pending',
    /** expected_setting_revision is not the current one. */
    'settings_changed',
    'not_pending',
    /** The co-signer's `seen` values differ from the pending row: they attest to what they read. */
    'stale_view',
];
/* -------------------------------------------------------------------------- */
/* Shared shapes                                                               */
/* -------------------------------------------------------------------------- */
const Count = z.number().int().min(0);
const Revision = z.number().int().min(1);
/** A `numeric(4,3)` rate string as integer thousandths, exactly — `"0.935"` → 935. */
export function thresholdMilli(value) {
    return Number(value.slice(0, 1)) * 1000 + Number(value.slice(2));
}
/** The notes' CHECK (`1..500`, never blank) on every policy table. */
export const VerificationPolicyNoteSchema = z
    .string()
    .min(1)
    .max(500)
    .refine((value) => value.trim().length > 0, 'Must contain a non-whitespace character');
/** A bigint row id as decimal text — `bigintIdTextSchema`, so a malformed id is a 400 before any statement. */
const RowIdSchema = bigintIdTextSchema('expected a row id');
/**
 * CLM-107's grain, extended by R-108-6: the schema version and model of an
 * extraction, and — for an admin field — the definition revision it was asked
 * under (`null` for a governed field, which never resets on an admin edit).
 */
export const VerificationGrainSchema = z
    .object({
    extraction_schema_version: z.string().min(1).max(20),
    extraction_model: z.string().min(1).max(120),
    definition_revision: Revision.nullable(),
})
    .strict();
/**
 * The org-wide basis at a grain (`fn_field_policy_basis`). `sample_size` is
 * Verified + Corrected; Rejected is counted outside it (R-107-4). The rate is
 * display only — the decision compares the counts (R-107-8).
 */
export const VerificationBasisSchema = z
    .object({
    verified_count: Count,
    corrected_count: Count,
    rejected_count: Count,
    sample_size: Count,
    accuracy_rate: ThresholdSchema.nullable(),
    last_decision_utc: IsoTimestampUtcSchema.nullable(),
})
    .strict()
    .superRefine((basis, ctx) => {
    if (basis.sample_size !== basis.verified_count + basis.corrected_count) {
        ctx.addIssue({ code: 'custom', path: ['sample_size'], message: 'sample_size is verified_count + corrected_count' });
    }
    if ((basis.accuracy_rate === null) !== (basis.sample_size === 0)) {
        ctx.addIssue({ code: 'custom', path: ['accuracy_rate'], message: 'accuracy_rate is null exactly at sample_size 0 — never 0' });
    }
    if ((basis.last_decision_utc === null) !== (basis.sample_size === 0)) {
        ctx.addIssue({ code: 'custom', path: ['last_decision_utc'], message: 'the sample span is null exactly at sample_size 0' });
    }
});
/* -------------------------------------------------------------------------- */
/* Settings (R-108-10)                                                         */
/* -------------------------------------------------------------------------- */
/** One `clm.verification_policy_setting` row — a proposal and what became of it. */
export const VerificationSettingChangeSchema = z
    .object({
    setting_change_id: RowIdSchema,
    status: VerificationSettingStatusSchema,
    threshold: ThresholdSchema,
    sample_rate: ThresholdSchema,
    stale_after_days: z
        .number()
        .int()
        .min(VERIFICATION_SETTING_BOUNDS.stale_after_days_min)
        .max(VERIFICATION_SETTING_BOUNDS.stale_after_days_max),
    /** The effective revision the proposer saw; `null` when none was effective. */
    supersedes_revision: Revision.nullable(),
    /** Set exactly when effective: `(supersedes_revision ?? 0) + 1`. */
    setting_revision: Revision.nullable(),
    proposed_by_upn: UpnSchema,
    proposed_utc: IsoTimestampUtcSchema,
    proposal_note: VerificationPolicyNoteSchema,
    /** The co-signer or decliner (a Legal holder), the withdrawer (an Admin), or — under
     * CLM-127's flag-gated path — the same Admin who proposed it, self-certifying. */
    decided_by_upn: UpnSchema.nullable(),
    decided_utc: IsoTimestampUtcSchema.nullable(),
    decision_note: VerificationPolicyNoteSchema.nullable(),
    /** CLM-127, D-127-1 — true exactly when `decided_by_upn` is the proposer, self-certifying
     * under the flag; false for withdrawn, for pending (no decision yet) and for a genuine
     * co-sign or decline. Always present, never inferred from the UPNs alone. */
    is_self_certified: z.boolean(),
})
    .strict()
    .superRefine((change, ctx) => {
    if ((change.status === 'effective') !== (change.setting_revision !== null)) {
        ctx.addIssue({ code: 'custom', path: ['setting_revision'], message: 'a change has a setting_revision exactly when it is effective' });
    }
    if (change.setting_revision !== null && change.setting_revision !== (change.supersedes_revision ?? 0) + 1) {
        ctx.addIssue({ code: 'custom', path: ['setting_revision'], message: 'an effective change is the revision after the one its proposer saw' });
    }
    if ((change.status === 'pending') !== (change.decided_by_upn === null && change.decided_utc === null)) {
        ctx.addIssue({ code: 'custom', path: ['decided_by_upn'], message: 'a change is undecided exactly while pending' });
    }
    if (change.status === 'declined' && change.decision_note === null) {
        ctx.addIssue({ code: 'custom', path: ['decision_note'], message: 'a decline carries its reason' });
    }
    if (!change.is_self_certified &&
        (change.status === 'effective' || change.status === 'declined') &&
        change.decided_by_upn !== null &&
        change.decided_by_upn.toLowerCase() === change.proposed_by_upn.toLowerCase()) {
        ctx.addIssue({ code: 'custom', path: ['decided_by_upn'], message: 'the co-signer is never the proposer (D-2.1-7)' });
    }
    /* CLM-127, D-127-1 — the reverse: a self-certified row can only ever be the
     * proposer's own decision (mirrors ck_verification_policy_setting_two_people's
     * CASE in spirit — the database compares UPN and object id, the wire only UPN,
     * since object ids never leave the server). Self-certify never declines its own
     * proposal (R-127-7), so the status is always effective. */
    if (change.is_self_certified) {
        if (change.status !== 'effective') {
            ctx.addIssue({ code: 'custom', path: ['is_self_certified'], message: 'self-certify only ever makes a change effective, never declined, withdrawn or pending (D-127-1)' });
        }
        if (change.decided_by_upn === null || change.decided_by_upn.toLowerCase() !== change.proposed_by_upn.toLowerCase()) {
            ctx.addIssue({ code: 'custom', path: ['decided_by_upn'], message: 'a self-certified change is always the proposer’s own decision (D-127-1)' });
        }
    }
    if (thresholdMilli(change.threshold) <= thresholdMilli(VERIFICATION_SETTING_BOUNDS.threshold_above)) {
        ctx.addIssue({ code: 'custom', path: ['threshold'], message: 'the threshold is above 0.500' });
    }
    if (thresholdMilli(change.sample_rate) < thresholdMilli(VERIFICATION_SETTING_BOUNDS.sample_rate_min)) {
        ctx.addIssue({ code: 'custom', path: ['sample_rate'], message: 'the sample rate is at least 0.050 (NFR-16 forbids 0)' });
    }
});
/**
 * The settings card's data. `current` is the effective revision, or `null`:
 * auto-approval is **off** until the first co-sign (there is no seed row).
 * `history` is every change, newest first, and includes `current` and `pending`.
 */
const settingsCardShape = {
    current: VerificationSettingChangeSchema.nullable(),
    pending: VerificationSettingChangeSchema.nullable(),
    history: z.array(VerificationSettingChangeSchema),
};
function refineSettingsCard(card, ctx, at = []) {
    if (card.current !== null && card.current.status !== 'effective') {
        ctx.addIssue({ code: 'custom', path: [...at, 'current'], message: 'current is the effective revision' });
    }
    if (card.pending !== null && card.pending.status !== 'pending') {
        ctx.addIssue({ code: 'custom', path: [...at, 'pending'], message: 'pending is the pending change' });
    }
    const pendingInHistory = card.history.filter((change) => change.status === 'pending');
    if (pendingInHistory.length !== (card.pending === null ? 0 : 1)
        || (card.pending !== null && pendingInHistory[0]?.setting_change_id !== card.pending.setting_change_id)) {
        ctx.addIssue({ code: 'custom', path: [...at, 'history'], message: 'at most one pending change, and it is pending' });
    }
    const effective = card.history.filter((change) => change.setting_revision !== null);
    const latest = effective.reduce((best, change) => (best === null || (change.setting_revision ?? 0) > (best.setting_revision ?? 0) ? change : best), null);
    if ((latest?.setting_change_id ?? null) !== (card.current?.setting_change_id ?? null)) {
        ctx.addIssue({ code: 'custom', path: [...at, 'current'], message: 'current is the highest effective revision in history' });
    }
    card.history.forEach((change, index) => {
        const newer = card.history[index - 1];
        if (newer !== undefined && BigInt(newer.setting_change_id) <= BigInt(change.setting_change_id)) {
            ctx.addIssue({ code: 'custom', path: [...at, 'history', index], message: 'history is newest first' });
        }
    });
}
/* -------------------------------------------------------------------------- */
/* verification-policy — the page                                              */
/* -------------------------------------------------------------------------- */
/** No parameters. `.strict()`, so a `scope` or `threshold` key is a 400, never ignored. */
export const VerificationPolicyRequestSchema = z.object({}).strict();
/**
 * Legal's decision on an opt-in revision, as the page shows it — or, under
 * CLM-127's flag-gated path, the same Admin's own self-certification.
 * `is_self_certified` is always present and always distinguishes the two: a
 * reader never has to guess which happened from the UPN alone (D-127-1).
 */
export const VerificationPolicyCosignSchema = z
    .object({
    decision: VerificationCosignDecisionSchema,
    decided_by_upn: UpnSchema,
    decided_utc: IsoTimestampUtcSchema,
    is_self_certified: z.boolean(),
})
    .strict();
/**
 * One field's row. The fields are the governed list plus every admin key ever
 * added; `classification.contract_type` (CLM-110's proposal) is never listed —
 * it has no field row, so no policy applies to it.
 */
export const VerificationPolicyFieldSchema = z
    .object({
    field_name: FieldNameSchema,
    /** `HIGH_STAKES_FIELDS` — always reviewed by a person (D-2.1-5). */
    is_high_stakes: z.boolean(),
    is_admin_field: z.boolean(),
    /** An admin field since deactivated (CLM-109). */
    is_retired: z.boolean(),
    /** The grain of the field's latest pipeline extraction; `null` when never extracted. */
    grain: VerificationGrainSchema.nullable(),
    /** The org-wide basis at `grain` (zeros when there is none). */
    basis: VerificationBasisSchema,
    policy: z
        .object({
        mode: VerificationPolicyModeSchema,
        /** `0` = no row: the `threshold` default. */
        policy_revision: z.number().int().min(0),
        changed_by_upn: UpnSchema.nullable(),
        changed_utc: IsoTimestampUtcSchema.nullable(),
        change_note: VerificationPolicyNoteSchema.nullable(),
        /** An opt-in taken at another grain: it behaves as `threshold` (R-108-3). */
        opt_in_lapsed: z.boolean(),
        /** `null` for a mode other than `auto_approve`, or before Legal decided. */
        cosign: VerificationPolicyCosignSchema.nullable(),
    })
        .strict(),
    effective_state: VerificationPolicyStateSchema,
    /** `last_decision_utc + stale_after_days`, exactly when `effective_state` is `stale` (R-108-8). */
    stale_since_utc: IsoTimestampUtcSchema.nullable(),
    /**
     * Field rows enqueued in the last 30 days, by what the policy did: settled
     * by policy, sent to a person as the sample, and sent to a person for any
     * other reason. Disjoint; together, every AI-derived row of the window that
     * carries a disposition.
     */
    last_30_days: z.object({ auto_approved: Count, sampled: Count, queued: Count }).strict(),
    /** Every row of the field now `AutoApproved` — "N values auto-approved under earlier policy" (R-108-17). */
    auto_approved_total: Count,
})
    .strict()
    .superRefine((field, ctx) => {
    const { policy } = field;
    const state = field.effective_state;
    if ((policy.policy_revision === 0) !== (policy.changed_by_upn === null)) {
        ctx.addIssue({ code: 'custom', path: ['policy', 'changed_by_upn'], message: 'a setter is named exactly when a policy row exists' });
    }
    if (policy.policy_revision === 0 && (policy.mode !== 'threshold' || policy.changed_utc !== null || policy.change_note !== null)) {
        ctx.addIssue({ code: 'custom', path: ['policy'], message: 'no policy row is the threshold default, with no change recorded' });
    }
    if ((policy.cosign !== null || policy.opt_in_lapsed) && policy.mode !== 'auto_approve') {
        ctx.addIssue({ code: 'custom', path: ['policy'], message: 'only an opt-in is co-signed or lapses' });
    }
    if (field.is_high_stakes) {
        if (policy.policy_revision !== 0) {
            ctx.addIssue({ code: 'custom', path: ['policy'], message: 'a high-stakes field accepts no policy row (D-2.1-5)' });
        }
        if (state !== 'high_stakes_exempt' && state !== 'not_extracted') {
            ctx.addIssue({ code: 'custom', path: ['effective_state'], message: 'a high-stakes field is always reviewed by a person (D-2.1-5)' });
        }
        if (field.auto_approved_total !== 0 || field.last_30_days.auto_approved !== 0) {
            ctx.addIssue({ code: 'custom', path: ['auto_approved_total'], message: 'a high-stakes field is never auto-approved (D-2.1-5)' });
        }
    }
    if (state === 'high_stakes_exempt' && !field.is_high_stakes) {
        ctx.addIssue({ code: 'custom', path: ['effective_state'], message: 'high_stakes_exempt is the high-stakes state only' });
    }
    if (field.is_retired && !field.is_admin_field) {
        ctx.addIssue({ code: 'custom', path: ['is_retired'], message: 'only an admin field retires' });
    }
    if ((field.is_retired || field.grain === null) && state !== 'not_extracted') {
        ctx.addIssue({ code: 'custom', path: ['effective_state'], message: 'a field never extracted, or retired, is not_extracted' });
    }
    if (state === 'not_extracted' && field.grain !== null && !field.is_retired) {
        ctx.addIssue({ code: 'custom', path: ['effective_state'], message: 'not_extracted means no extraction, or a retired field' });
    }
    if (field.grain === null && field.basis.sample_size + field.basis.rejected_count !== 0) {
        ctx.addIssue({ code: 'custom', path: ['basis'], message: 'with no grain there is no basis' });
    }
    if (field.grain !== null && (field.grain.definition_revision !== null) !== field.is_admin_field) {
        ctx.addIssue({ code: 'custom', path: ['grain', 'definition_revision'], message: 'an admin field’s grain names its definition revision (R-108-6); a governed field’s does not' });
    }
    if (state === 'admin_pinned' && policy.mode !== 'always_verify') {
        ctx.addIssue({ code: 'custom', path: ['effective_state'], message: 'admin_pinned is the always_verify state' });
    }
    if (policy.mode === 'always_verify' && state !== 'admin_pinned' && state !== 'not_extracted') {
        ctx.addIssue({ code: 'custom', path: ['effective_state'], message: 'an always_verify field is admin_pinned' });
    }
    if ((field.stale_since_utc !== null) !== (state === 'stale')) {
        ctx.addIssue({ code: 'custom', path: ['stale_since_utc'], message: 'stale_since_utc is set exactly when the field is stale' });
    }
});
/** States that can only arise once settings are effective — decision steps 4–8 (spec §3.3). */
const SETTINGS_DEPENDENT_STATES = ['auto', 'too_few_decisions', 'stale', 'below_threshold'];
export const VerificationPolicyResponseSchema = z
    .object({
    generated_utc: IsoTimestampUtcSchema,
    /** D-2.1-2's 30, as the server applied it. */
    minimum_sample: z.literal(VERIFICATION_MINIMUM_SAMPLE),
    settings: z.object(settingsCardShape).strict().superRefine((card, ctx) => refineSettingsCard(card, ctx)),
    fields_withheld: z.enum(VERIFICATION_POLICY_FIELDS_WITHHELD).nullable(),
    /** `null` exactly when `fields_withheld` is set: a practice-scoped Admin (R-108-12). */
    fields: z.array(VerificationPolicyFieldSchema).nullable(),
    /** CLM-127, D-127-1 — `CLM_API_VERIFICATION_POLICY_ALLOW_SELF_CERTIFY`, as the server read
     * it; read from the request's own context, never a database row, so the web knows whether
     * to offer the self-certify buttons with no second round-trip. */
    self_certify_enabled: z.boolean(),
})
    .strict()
    .superRefine((body, ctx) => {
    if ((body.fields === null) !== (body.fields_withheld !== null)) {
        ctx.addIssue({ code: 'custom', path: ['fields'], message: 'fields is null exactly when fields_withheld says why' });
    }
    const fields = body.fields ?? [];
    const names = new Set();
    fields.forEach((field, index) => {
        if (names.has(field.field_name)) {
            ctx.addIssue({ code: 'custom', path: ['fields', index, 'field_name'], message: 'each field is listed once' });
        }
        names.add(field.field_name);
        if (field.field_name === 'classification.contract_type') {
            ctx.addIssue({ code: 'custom', path: ['fields', index, 'field_name'], message: 'a type proposal has no field row and no policy' });
        }
        const state = field.effective_state;
        if (state === 'auto' && field.basis.sample_size < body.minimum_sample) {
            ctx.addIssue({ code: 'custom', path: ['fields', index, 'effective_state'], message: 'auto needs the minimum sample (D-2.1-2)' });
        }
        if (state === 'too_few_decisions' && field.basis.sample_size >= body.minimum_sample) {
            ctx.addIssue({ code: 'custom', path: ['fields', index, 'effective_state'], message: 'too_few_decisions is below the minimum sample' });
        }
        if (body.settings.current === null && SETTINGS_DEPENDENT_STATES.includes(state)) {
            ctx.addIssue({ code: 'custom', path: ['fields', index, 'effective_state'], message: 'with no co-signed settings a field is not_configured' });
        }
        if (body.settings.current !== null && state === 'not_configured') {
            ctx.addIssue({ code: 'custom', path: ['fields', index, 'effective_state'], message: 'not_configured only while no settings are effective' });
        }
    });
});
/* -------------------------------------------------------------------------- */
/* set-field-verification-policy, cosign-field-verification-policy             */
/* -------------------------------------------------------------------------- */
/**
 * One `clm.verification_policy` revision, as written: the mode, the basis it
 * was taken on (captured server-side — FR-31's audit row carries the same
 * figures), the setter and, for an opt-in, Legal's decision on it.
 */
export const FieldPolicyRevisionSchema = z
    .object({
    verification_policy_id: RowIdSchema,
    field_name: FieldNameSchema,
    policy_revision: Revision,
    supersedes_revision: Revision.nullable(),
    mode: VerificationPolicyModeSchema,
    /** The basis grain; `null` for a field never extracted. */
    basis_grain: VerificationGrainSchema.nullable(),
    basis: VerificationBasisSchema,
    change_note: VerificationPolicyNoteSchema.nullable(),
    changed_by_upn: UpnSchema,
    changed_utc: IsoTimestampUtcSchema,
    cosign: VerificationPolicyCosignSchema.extend({ decision_note: VerificationPolicyNoteSchema.nullable() }).strict().nullable(),
})
    .strict()
    .superRefine((revision, ctx) => {
    const expected = revision.policy_revision === 1 ? null : revision.policy_revision - 1;
    if (revision.supersedes_revision !== expected) {
        ctx.addIssue({ code: 'custom', path: ['supersedes_revision'], message: 'each revision supersedes the one before it' });
    }
    if (revision.basis_grain === null && revision.basis.sample_size + revision.basis.rejected_count !== 0) {
        ctx.addIssue({ code: 'custom', path: ['basis'], message: 'with no grain there is no basis' });
    }
    if (revision.mode === 'auto_approve' &&
        (revision.basis_grain === null || revision.basis.sample_size < VERIFICATION_MINIMUM_SAMPLE || revision.change_note === null)) {
        ctx.addIssue({ code: 'custom', path: ['mode'], message: 'an opt-in needs a grain, the minimum sample and a note (D-2.1-2)' });
    }
    if (revision.cosign !== null) {
        if (revision.mode !== 'auto_approve') {
            ctx.addIssue({ code: 'custom', path: ['cosign'], message: 'only an opt-in is co-signed' });
        }
        if (revision.cosign.decision === 'decline' && revision.cosign.decision_note === null) {
            ctx.addIssue({ code: 'custom', path: ['cosign', 'decision_note'], message: 'a decline carries its reason' });
        }
        const sameUpn = revision.cosign.decided_by_upn.toLowerCase() === revision.changed_by_upn.toLowerCase();
        if (!revision.cosign.is_self_certified && sameUpn) {
            ctx.addIssue({ code: 'custom', path: ['cosign', 'decided_by_upn'], message: 'the co-signer is never the setter (R-108-24)' });
        }
        /* CLM-127, D-127-1 — the reverse: self-certify never declines its own just-made opt-in
         * (R-127-7), and is always the same person who set it. */
        if (revision.cosign.is_self_certified) {
            if (revision.cosign.decision !== 'cosign') {
                ctx.addIssue({ code: 'custom', path: ['cosign', 'is_self_certified'], message: 'self-certify only ever co-signs, never declines (D-127-1)' });
            }
            if (!sameUpn) {
                ctx.addIssue({ code: 'custom', path: ['cosign', 'decided_by_upn'], message: 'a self-certified opt-in is always the setter’s own decision (D-127-1)' });
            }
        }
    }
});
/**
 * A new revision of one field's policy. No counts and no basis: the server
 * captures the org-wide basis itself. `change_note` is required for
 * `auto_approve`, and refused as `note_required` by the route rather than here,
 * so the refusal is the coded one.
 */
export const SetFieldVerificationPolicyRequestSchema = z
    .object({
    field_name: FieldNameSchema,
    /** The revision the caller saw; `0` = no row yet. A stale page is 409 policy_changed, never an overwrite. */
    expected_policy_revision: z.number().int().min(0),
    mode: VerificationPolicyModeSchema,
    change_note: VerificationPolicyNoteSchema.optional(),
})
    .strict();
/**
 * The revision written, the field's effective state now — for an `auto_approve`
 * revision, as it stands **before** Legal co-signs (it behaves as `threshold`
 * until then) — and the `0046` Create receipt.
 */
export const SetFieldVerificationPolicyResponseSchema = z
    .object({
    policy: FieldPolicyRevisionSchema,
    effective_state: VerificationPolicyStateSchema,
    audit: AuditReceiptSchema,
})
    .strict()
    .superRefine((body, ctx) => {
    if (body.policy.cosign !== null) {
        ctx.addIssue({ code: 'custom', path: ['policy', 'cosign'], message: 'a new revision has no co-sign yet' });
    }
    if (body.effective_state === 'high_stakes_exempt') {
        ctx.addIssue({ code: 'custom', path: ['effective_state'], message: 'a high-stakes field accepts no policy row (D-2.1-5)' });
    }
});
/**
 * Legal's co-sign or decline of one opt-in revision (R-108-24). The revision is
 * the attestation: its basis is immutable, so the co-signer has read exactly
 * what it records. A decline needs a note (`note_required`, coded by the route).
 */
export const CosignFieldVerificationPolicyRequestSchema = z
    .object({
    field_name: FieldNameSchema,
    /** Must be the field's current revision: otherwise 409 policy_changed. */
    policy_revision: Revision,
    decision: VerificationCosignDecisionSchema,
    decision_note: VerificationPolicyNoteSchema.optional(),
})
    .strict();
export const CosignFieldVerificationPolicyResponseSchema = z
    .object({
    policy: FieldPolicyRevisionSchema,
    effective_state: VerificationPolicyStateSchema,
    audit: AuditReceiptSchema,
})
    .strict()
    .superRefine((body, ctx) => {
    if (body.policy.cosign === null || body.policy.mode !== 'auto_approve') {
        ctx.addIssue({ code: 'custom', path: ['policy', 'cosign'], message: 'the revision returned is the opt-in, carrying the decision just made' });
    }
});
/* -------------------------------------------------------------------------- */
/* propose / withdraw / view / co-sign the settings                            */
/* -------------------------------------------------------------------------- */
/**
 * A proposed threshold, sample rate and stale window. The bounds are checked
 * by the route (`threshold_out_of_bounds` …), not here, so the refusal names
 * the bound; `stale_after_days` is loose for the same reason. All three need
 * the co-sign (R-108-10). The pre-filled 0.900 / 0.200 / 90 live on the page.
 */
export const ProposeVerificationSettingsRequestSchema = z
    .object({
    /** The effective revision the caller saw; `0` = none (auto-approval off). */
    expected_setting_revision: z.number().int().min(0),
    threshold: ThresholdSchema,
    sample_rate: ThresholdSchema,
    stale_after_days: z.number().int().min(1).max(3650),
    proposal_note: VerificationPolicyNoteSchema,
})
    .strict();
export const ProposeVerificationSettingsResponseSchema = z
    .object({ change: VerificationSettingChangeSchema, audit: AuditReceiptSchema })
    .strict()
    .refine((body) => body.change.status === 'pending', { message: 'a proposal is pending', path: ['change', 'status'] });
export const WithdrawVerificationSettingsRequestSchema = z
    .object({
    setting_change_id: RowIdSchema,
    /** Recorded as the row's decision_note. */
    note: VerificationPolicyNoteSchema.optional(),
})
    .strict();
export const WithdrawVerificationSettingsResponseSchema = z
    .object({ change: VerificationSettingChangeSchema, audit: AuditReceiptSchema })
    .strict()
    .refine((body) => body.change.status === 'withdrawn', { message: 'the change is withdrawn', path: ['change', 'status'] });
/** No parameters. */
export const VerificationSettingsRequestSchema = z.object({}).strict();
/** A current `auto_approve` revision with no decision — what Legal is asked to co-sign. */
export const PendingOptInSchema = z
    .object({
    field_name: FieldNameSchema,
    policy_revision: Revision,
    changed_by_upn: UpnSchema,
    changed_utc: IsoTimestampUtcSchema,
    /** An opt-in always carries its reason (ck_verification_policy_opt_in). */
    change_note: VerificationPolicyNoteSchema,
    grain: VerificationGrainSchema,
    /** The basis recorded on the revision — the org-wide figure it was taken on. */
    basis: VerificationBasisSchema,
    /** The caller set this opt-in (UPN in any case, or object id): they cannot co-sign it. */
    caller_is_setter: z.boolean(),
})
    .strict()
    .refine((optIn) => optIn.basis.sample_size >= VERIFICATION_MINIMUM_SAMPLE, {
    message: 'an opt-in rests on at least the minimum sample (D-2.1-2)',
    path: ['basis', 'sample_size'],
});
export const VerificationSettingsResponseSchema = z
    .object({
    generated_utc: IsoTimestampUtcSchema,
    ...settingsCardShape,
    /** The caller proposed the pending change (UPN in any case, or object id): they cannot co-sign it. */
    caller_is_proposer: z.boolean(),
    /** Scope-All only (R-108-12): how many fields would auto-approve now, and under the pending values. */
    impact: z
        .object({ fields_auto_now: Count, fields_auto_at_pending: Count.nullable() })
        .strict()
        .nullable(),
    /** Scope-All only: the opt-ins awaiting a co-sign, which carry org-wide counts. */
    pending_opt_ins: z.array(PendingOptInSchema).nullable(),
    /** CLM-127, D-127-1 — as `VerificationPolicyResponseSchema`'s own field: whether the
     * flag-gated self-certify action is reachable at all right now. */
    self_certify_enabled: z.boolean(),
})
    .strict()
    .superRefine((body, ctx) => {
    refineSettingsCard(body, ctx);
    if (body.pending === null && body.caller_is_proposer) {
        ctx.addIssue({ code: 'custom', path: ['caller_is_proposer'], message: 'there is no pending change to have proposed' });
    }
    if ((body.impact === null) !== (body.pending_opt_ins === null)) {
        ctx.addIssue({ code: 'custom', path: ['impact'], message: 'impact and pending_opt_ins are both computed for a Scope-All caller, or neither' });
    }
    if (body.impact !== null && (body.impact.fields_auto_at_pending === null) !== (body.pending === null)) {
        ctx.addIssue({ code: 'custom', path: ['impact', 'fields_auto_at_pending'], message: 'the pending impact exists exactly when a change is pending' });
    }
});
/**
 * Co-sign or decline the pending change. `seen` is what the co-signer read:
 * a mismatch with the row is 409 `stale_view` — they attest to the values they
 * were shown, as `seen_extracted_value` does for a field.
 */
export const CosignVerificationSettingsRequestSchema = z
    .object({
    setting_change_id: RowIdSchema,
    decision: VerificationCosignDecisionSchema,
    seen: z
        .object({
        threshold: ThresholdSchema,
        sample_rate: ThresholdSchema,
        stale_after_days: z.number().int().min(1).max(3650),
    })
        .strict(),
    decision_note: VerificationPolicyNoteSchema.optional(),
})
    .strict();
export const CosignVerificationSettingsResponseSchema = z
    .object({ change: VerificationSettingChangeSchema, audit: AuditReceiptSchema })
    .strict()
    .refine((body) => body.change.status === 'effective' || body.change.status === 'declined', {
    message: 'a co-sign makes the change effective or declines it',
    path: ['change', 'status'],
});
/* -------------------------------------------------------------------------- */
/* self-certify-verification-settings, self-certify-field-verification-policy  */
/* CLM-127, D-127-1 — flag-gated, admin:configure + Scope-All, no Legal co-sign */
/* -------------------------------------------------------------------------- */
/**
 * The same fields a proposal takes (R-127-14) — a type alias, not a
 * redeclaration, so the two request shapes can never drift apart.
 * `boundsRefusal` and the web's draft-validity helpers serve both unchanged.
 */
export const SelfCertifyVerificationSettingsRequestSchema = ProposeVerificationSettingsRequestSchema;
/**
 * The same shape `ProposeVerificationSettingsResponseSchema` /
 * `CosignVerificationSettingsResponseSchema` already use (R-127-10 — no new
 * response shape): the written, now-effective change, self-certified, and the
 * `0046` receipt of the decisive act (the decision; the proposal's own receipt
 * is a second `Create` row in the same transaction, read but not carried on
 * the wire — the full trail is the database's, not this response's, to prove).
 */
export const SelfCertifyVerificationSettingsResponseSchema = z
    .object({ change: VerificationSettingChangeSchema, audit: AuditReceiptSchema })
    .strict()
    .refine((body) => body.change.status === 'effective' && body.change.is_self_certified, {
    message: 'a self-certified settings change is always effective and always marked (D-127-1)',
    path: ['change', 'is_self_certified'],
});
/**
 * `{field_name, expected_policy_revision, change_note}` — **no `mode` field at
 * all** (R-127-14): the mode is fixed to `auto_approve` server-side, since no
 * other mode ever needs a co-sign (R-108-3), which also makes it impossible to
 * ask this endpoint to "self-certify" a plain `threshold` / `always_verify`
 * change — the wire shape has nowhere to say one.
 */
export const SelfCertifyFieldVerificationPolicyRequestSchema = z
    .object({
    field_name: FieldNameSchema,
    expected_policy_revision: z.number().int().min(0),
    change_note: VerificationPolicyNoteSchema,
})
    .strict();
/** The same shape `SetFieldVerificationPolicyResponseSchema` already uses (R-127-10). */
export const SelfCertifyFieldVerificationPolicyResponseSchema = z
    .object({
    policy: FieldPolicyRevisionSchema,
    effective_state: VerificationPolicyStateSchema,
    audit: AuditReceiptSchema,
})
    .strict()
    .superRefine((body, ctx) => {
    if (body.policy.mode !== 'auto_approve') {
        ctx.addIssue({ code: 'custom', path: ['policy', 'mode'], message: 'self-certify only ever takes an auto_approve opt-in (R-127-14)' });
    }
    if (body.policy.cosign === null || !body.policy.cosign.is_self_certified || body.policy.cosign.decision !== 'cosign') {
        ctx.addIssue({ code: 'custom', path: ['policy', 'cosign'], message: 'the revision returned carries its own self-certified co-sign (D-127-1)' });
    }
});
//# sourceMappingURL=verification-policy.js.map